import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';
import { 
  Character,
  CharacterCondition,
  CharacterGift,
  Condition,
  Gift,
  PseudoItem,
} from "@dndModels";
import { ModelBase } from "@models";

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';
import { makeStyles } from "@utilities/theme_utilities";

import RefreshButton from "@components/input/buttons/icon_buttons/RefreshButton";
import LoadingBox from "@components/input/LoadingBox";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  char: Character;
  onChange: (changed: Character, do_main: boolean) => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterMailInput({
  char,
  onChange
}: Props) {
  // const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [expanded_id, setExpandedId] = useState("");
    
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

  const load = () => {
    setLoading(true);
    char_util.getReceivables({ char }).then(() => {
      char.subscription_holder.receivablesChanged();
      setLoading(false);
    });
  }

  const { classes, cx } = useStyles();
  
  if (char.campaign) {
    const campaign = char.campaign;
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>Mail</Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <RefreshButton
            onClick={load}
          />
        </Grid>
        <Grid
          className="centered"
          size={{
            xs: 4
          }}>
          { char.receivables.length > 0 && 
            <DandDButtonBox
              name="Mark All Received"
              disabled={processing || char.receivables.filter(o => o.received_dt === 0).length === 0}
              onClick={async () => {
                setProcessing(true);
                for (const receivable of char.receivables.filter(o => o.received_dt === 0)) {
                  const new_obj = receivable;
                  new_obj.received_dt = common_util.get_time_number();
                  char.subscription_holder.receivablesChanged();
                  await api.updateObject("character_receivable", new_obj);
                }
                setProcessing(false);
              }}
            />
          }
        </Grid>
        <Grid
          className="centered"
          size={{
            xs: 4
          }}>
          { char.receivables.length > 0 && 
            <DeleteButton
              label="Delete All"
              disabled={processing}
              onClick={async () => {
                setProcessing(true);
                for (const receivable of char.receivables) {
                  const new_obj = receivable;
                  char.receivables = char.receivables.filter(o => o._id !== new_obj._id);
                  char.subscription_holder.receivablesChanged();
                  await api.deleteObject("character_receivable", new_obj);
                }
                setProcessing(false);
              }}
            />
          }
        </Grid>
        { loading ? 
          <Grid
            size={{
              xs: 12
            }}>
            <LoadingBox />
          </Grid>
        : char.receivables.length === 0 ?
          <Grid
            className="padded centered"
            size={{
              xs: 12
            }}>
            You have no mail
          </Grid>
        :
          <>
            { char.receivables.map(receivable => {
              // console.log(receivable);
              // console.log(campaign.characters);
              const from_char = campaign.characters.find(o => o._id === receivable.from_id);
              const from = from_char ? from_char.name : (receivable.from_id === "DM" ? "DM" : "Missing Character");
              
              let obj: ModelBase | PseudoItem | undefined = undefined;
              let description = " ";
              if (receivable.items.length > 0) {
                const items = receivable.items;
                if (items.length === 1) {
                  const item = items[0];
                  if (item.type === "Condition") {
                    // console.log(char_util.conditions);
                    obj = data_util.conditions.find(o => o._id === item.obj_id);
                    if (obj instanceof Condition) {
                      if (obj.max_level > 1) {
                        description = item.count < 0 ? `Remove ${item.count * -1} levels of ` : `Add ${item.count} levels of `;
                      } else {
                        description = item.count === -1 ? "Remove " : "Apply ";
                      }
                      description += obj.name;
                    }
                  } else if (item.type === "Gift") {
                    // console.log(char_util.conditions);
                    obj = data_util.gifts.find(o => o._id === item.obj_id);
                    if (obj instanceof Gift) {
                      if (obj.max_level > 1) {
                        description = item.count < 0 ? `Remove ${item.count * -1} levels of ` : `Add ${item.count} levels of `;
                      } else {
                        description = item.count === -1 ? "Remove " : "Apply ";
                      }
                      description += obj.name;
                    }
                  } else if (item.type === "Item Enhancement") {
                  } else if (item.type === "Item") {
                    obj = char_util.getPseudoItem(item.obj_id);
                    if (obj) {
                      description = obj.name;
                    }
                  }
                } else {
                  for (const item of items) {
                    obj = char_util.getPseudoItem(item.obj_id);
                    if (obj) {
                      if (description.length > 0) {
                        description += ", ";
                      }
                      description += obj.name;
                    }
                  }
                }
              }
              description = receivable.message + "\n" + description;
              return (
                <Grid
                  key={receivable._id}
                  size={{
                    xs: 12
                  }}>
                  <div className="padded">
                    <Grid container spacing={0} direction="row" className={cx(classes.root, "padded")}>
                      <Grid
                        className="clickable"
                        onClick={() => {
                          setExpandedId(expanded_id === receivable._id ? "": receivable._id);
                        }}
                        size={{
                          xs: 4
                        }}>
                        From&nbsp;{ from }:
                      </Grid>
                      <Grid
                        className="clickable"
                        onClick={() => {
                          setExpandedId(expanded_id === receivable._id ? "": receivable._id);
                        }}
                        size={{
                          xs: 4
                        }}>
                        <DisplayStringWithReplacements cfs={char} display_string={ description } />
                      </Grid>
                      <Grid
                        size={{
                          xs: 4
                        }}>
                        { receivable.items.length === 1 ? 
                          <>
                          { receivable.items[0].type === "Condition" ?
                            <DandDButtonBox
                              name="Apply"
                              disabled={processing}
                              onClick={ async () => {
                                if (obj instanceof Condition) {
                                  const condition = obj;
                                  const acf = char.alternate_condition_features.find(o => o.original_condition_id === condition._id);
                                  
                                  const cond_id = acf ? acf.alternate_condition_id : condition._id;
                                  // Apply the condition
                                  let char_cond = char.conditions.find(o => o.source_id === cond_id);
                                  let level = char_cond?.level??0; // char.condition_hash[cond_id]?.level??0;
                                  level = Math.max(level + receivable.items[0].count, 0);
                                  if (obj.max_level > 1) {
                                    level = Math.min(obj.max_level, level);
                                  } else if (level > 1) {
                                    level = 1;
                                  }
                                  if (level === 0) {
                                    if (char_cond) {
                                      char.spirit.conditions = char.conditions.filter(o => o.source_id !== cond_id);
                                    }
                                    // delete char.condition_hash[cond_id];
                                  } else {
                                    if (!char_cond) {
                                      await api.loadObject(obj);
                                      char_cond = new CharacterCondition();
                                      await char_cond.copy_source({ char, feature_source: obj });
                                      char.conditions.push(char_cond);
                                    }
                                    char_cond.level = level;
                                    // char.condition_hash[cond_id] = { level, source_true_id: "" };
                                  }
                                  char.changed();
                                  setProcessing(true);
                                  char.subscription_holder.recalculate();
                                  setProcessing(false);
                                }
                              }}
                            />
                          : receivable.items[0].type === "Gift" ?
                            <DandDButtonBox
                              name="Apply"
                              disabled={processing}
                              onClick={ async () => {
                                if (obj instanceof Gift) {
                                  await api.loadObject(obj);
                                  const gift = obj;
                                  const gift_id = gift._id;
                                  const char_gift = char.spirit.gifts.find(o => o.source_id === gift_id);
                                  if (!char_gift) {
                                    // Apply the gift
                                    const char_gift = new CharacterGift();
                                    await char_gift.copy_source({ char, feature_source: obj });
                                    char_gift.level = receivable.items[0].count;
                                    if (char_gift.level < 1) {
                                      char_gift.level = 1;
                                    }
                                    char.spirit.gifts.push(char_gift);
                                    char.changed();
                                    setProcessing(true);
                                    char.subscription_holder.recalculate();
                                    setProcessing(false);
                                  }
                                }
                              }}
                            />
                          : receivable.items[0].type === "Item" &&
                            <DandDButtonBox
                              name="Add to Inventory"
                              disabled={processing}
                              onClick={ async () => {
                                if (obj instanceof PseudoItem) {
                                  const pseudo_item = obj;
                                  await connect_util.addItem({ char, item: pseudo_item, count: receivable.items[0].count });
                                  char.subscription_holder.itemsChanged();
                                  char.changed();
                                }
                              }}
                            />
                          }
                          </>
                        : receivable.items.length > 1 &&
                          <>
                            <DandDButtonBox
                              name="Add to Inventory"
                              disabled={processing}
                              onClick={ async () => {
                                const items = receivable.items;
                                for (const item of items) {
                                  obj = char_util.getPseudoItem(item.obj_id);
                                  if (obj instanceof PseudoItem) {
                                    const pseudo_item = obj;
                                    await connect_util.addItem({ char, item: pseudo_item, count: item.count });
                                  }
                                }
                                char.subscription_holder.itemsChanged();
                                char.changed();
                              }}
                            />
                          </>
                        }
                      </Grid>
                      { expanded_id === receivable._id && 
                        <>
                          <Grid
                            style={{ border: "1px solid", backgroundColor: "black" }}
                            size={{
                              xs: 12
                            }}>
                            <div className="padded">
                              <Grid container spacing={0} direction="row">
                                <Grid
                                  className="centered"
                                  size={{
                                    xs: 6
                                  }}>
                                  { receivable.received_dt === 0 ?
                                    <DandDButtonBox
                                      name="Mark Received"
                                      disabled={processing}
                                      onClick={() => {
                                        setProcessing(true);
                                        const new_obj = receivable;
                                        new_obj.received_dt = common_util.get_time_number();
                                        char.subscription_holder.receivablesChanged();
                                        api.updateObject("character_receivable", new_obj).then(() => {
                                          setProcessing(false);
                                        });
                                      }}
                                    />
                                  :
                                    `Received ${common_util.time_string(receivable.received_dt)}`
                                  }
                                </Grid>
                                <Grid
                                  className="centered"
                                  size={{
                                    xs: 6
                                  }}>
                                  <DeleteButton
                                    disabled={processing}
                                    onClick={() => {
                                      setProcessing(true);
                                      const new_obj = receivable;
                                      char.receivables = char.receivables.filter(o => o._id !== new_obj._id);
                                      char.subscription_holder.receivablesChanged();
                                      api.deleteObject("character_receivable", new_obj).then(() => {
                                        setProcessing(false);
                                      });
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </div>
                          </Grid>
                        </>
                      }
                    </Grid>
                  </div>
                </Grid>
              );
            })}
          </>
        }
      </Grid>
    );
  }
  return <></>;
}
